import { useApi } from './useApi';

export interface SkillImage {
  url: string;
  annotation?: string;
}

export interface UserSkill {
  id: string;
  user_id: string;
  name: string;
  description?: string;
  prompt: string;
  prompt_template?: string;
  project_id?: string;
  cover?: string;
  category?: string;
  reference_images?: (string | SkillImage)[];
  assets?: (string | SkillImage)[];
  is_public: boolean;
  is_recommended: boolean;
  meta?: any;
  created_at: string;
  updated_at: string;
}

export interface CreateUserSkillDto {
  name: string;
  description?: string;
  prompt: string;
  promptTemplate?: string;
  projectId?: string;
  cover?: string;
  category?: string;
  referenceImages?: (string | SkillImage)[];
  assets?: (string | SkillImage)[];
  isPublic?: boolean;
  meta?: any;
}

export interface UpdateUserSkillDto {
  name?: string;
  description?: string;
  prompt?: string;
  promptTemplate?: string;
  projectId?: string;
  cover?: string;
  category?: string;
  referenceImages?: (string | SkillImage)[];
  assets?: (string | SkillImage)[];
  isPublic?: boolean;
  meta?: any;
}

export const useUserSkills = () => {
  const { request } = useApi();

  const listSkills = async (params: { limit?: number; offset?: number; q?: string } = {}) => {
    return request('/api/user-skills', {
      query: params,
    });
  };

  const listPublicSkills = async (
    params: { limit?: number; offset?: number; q?: string; category?: string } = {}
  ) => {
    return request('/api/user-skills/public', {
      query: params,
    });
  };

  const listRecommendedSkills = async (params: { limit?: number } = {}) => {
    return request('/api/user-skills/recommended', {
      query: params,
    });
  };

  const getSkill = async (id: string) => {
    return request(`/api/user-skills/${id}`);
  };

  const createSkill = async (data: CreateUserSkillDto) => {
    return request('/api/user-skills', {
      method: 'POST',
      body: data,
    });
  };

  const updateSkill = async (id: string, data: UpdateUserSkillDto) => {
    return request(`/api/user-skills/${id}`, {
      method: 'PUT',
      body: data,
    });
  };

  const forkSkill = async (id: string) => {
    return request(`/api/user-skills/${id}/fork`, {
      method: 'POST',
    });
  };

  const deleteSkill = async (id: string) => {
    return request(`/api/user-skills/${id}`, {
      method: 'DELETE',
    });
  };

  const analyzeSkillImage = async (assetIds: string | string[], instruction?: string) => {
    return request('/api/user-skills/analyze', {
      method: 'POST',
      body: {
        assetIds: Array.isArray(assetIds) ? assetIds : [assetIds],
        instruction,
      },
    });
  };

  const incrementSkillUsage = async (id: string) => {
    return request(`/api/user-skills/${id}/use`, {
      method: 'POST',
    });
  };

  return {
    listSkills,
    listPublicSkills,
    listRecommendedSkills,
    getSkill,
    createSkill,
    updateSkill,
    forkSkill,
    deleteSkill,
    analyzeSkillImage,
    incrementSkillUsage,
  };
};
